import {
  getBusinessObject
} from 'bpmn-js/lib/util/ModelUtil';

import {
  TextFieldEntry, isTextFieldEntryEdited,
  isFeelEntryEdited
} from '@bpmn-io/properties-panel';

import {
  getExtensionElementsList
} from '../../../utils/ExtensionElementsUtil';

import {
  createElement
} from '../../../utils/ElementUtil';
import {
  createOrUpdateFormalExpression
} from '../../../utils/FormalExpressionUtil';

import { useService } from '../../../hooks';

import { getSingletonEntryId } from '../utils/EntryIdUtil';

import { BpmnFeelEntry } from '../../../entries/BpmnFeelEntry';

export function MultiInstanceProps(props) {
  const {
    element
  } = props;

  if (!supportsMultiInstances(element)) {
    return [];
  }

  return [
    {
      id: getSingletonEntryId('zeebe:LoopCharacteristics', 'inputCollection'),
      component: InputCollection,
      isEdited: isFeelEntryEdited
    },
    {
      id: getSingletonEntryId('zeebe:LoopCharacteristics', 'inputElement'),
      component: InputElement,
      isEdited: isTextFieldEntryEdited
    },
    {
      id: getSingletonEntryId('zeebe:LoopCharacteristics', 'outputCollection'),
      component: OutputCollection,
      isEdited: isTextFieldEntryEdited
    },
    {
      id: getSingletonEntryId('zeebe:LoopCharacteristics', 'outputElement'),
      component: OutputElement,
      isEdited: isFeelEntryEdited
    },
    {
      id: getSingletonEntryId('bpmn:MultiInstanceLoopCharacteristics', 'completionCondition'),
      component: CompletionCondition,
      isEdited: isFeelEntryEdited
    }
  ];
}

function InputCollection(props) {
  const {
    element
  } = props;

  const commandStack = useService('commandStack');
  const bpmnFactory = useService('bpmnFactory');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return getProperty(element, 'inputCollection');
  };

  const setValue = (value) => {
    return setProperty(element, 'inputCollection', value, commandStack, bpmnFactory);
  };

  return BpmnFeelEntry({
    element,
    id: getSingletonEntryId('zeebe:LoopCharacteristics', 'inputCollection'),
    label: translate('Input collection'),
    feel: 'required',
    getValue,
    setValue,
    debounce
  });
}

function InputElement(props) {
  const {
    element
  } = props;

  const commandStack = useService('commandStack');
  const bpmnFactory = useService('bpmnFactory');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return getProperty(element, 'inputElement');
  };

  const setValue = (value) => {
    return setProperty(element, 'inputElement', value, commandStack, bpmnFactory);
  };

  return TextFieldEntry({
    element,
    id: getSingletonEntryId('zeebe:LoopCharacteristics', 'inputElement'),
    label: translate('Input element'),
    getValue,
    setValue,
    debounce
  });
}

function OutputCollection(props) {
  const {
    element
  } = props;

  const commandStack = useService('commandStack');
  const bpmnFactory = useService('bpmnFactory');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return getProperty(element, 'outputCollection');
  };

  const setValue = (value) => {
    return setProperty(element, 'outputCollection', value, commandStack, bpmnFactory);
  };

  return TextFieldEntry({
    element,
    id: getSingletonEntryId('zeebe:LoopCharacteristics', 'outputCollection'),
    label: translate('Output collection'),
    getValue,
    setValue,
    debounce
  });
}

function OutputElement(props) {
  const {
    element
  } = props;

  const commandStack = useService('commandStack');
  const bpmnFactory = useService('bpmnFactory');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    return getProperty(element, 'outputElement');
  };

  const setValue = (value) => {
    return setProperty(element, 'outputElement', value, commandStack, bpmnFactory);
  };

  return BpmnFeelEntry({
    element,
    id: getSingletonEntryId('zeebe:LoopCharacteristics', 'outputElement'),
    label: translate('Output element'),
    feel: 'required',
    getValue,
    setValue,
    debounce
  });
}

function CompletionCondition(props) {
  const {
    element
  } = props;

  const commandStack = useService('commandStack');
  const bpmnFactory = useService('bpmnFactory');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    const completionCondition = getCompletionCondition(element);
    return completionCondition && completionCondition.get('body');
  };

  const setValue = (value) => {
    return createOrUpdateFormalExpression(
      element,
      getLoopCharacteristics(element),
      'completionCondition',
      value,
      bpmnFactory,
      commandStack
    );
  };

  return BpmnFeelEntry({
    element,
    id: getSingletonEntryId('bpmn:MultiInstanceLoopCharacteristics', 'completionCondition'),
    label: translate('Completion condition'),
    feel: 'required',
    getValue,
    setValue,
    debounce
  });
}

// helper ///////////////////////

function getLoopCharacteristics(element) {
  const businessObject = getBusinessObject(element);
  return businessObject.get('loopCharacteristics');
}

function getZeebeLoopCharacteristics(loopCharacteristics) {
  const extensionElements = getExtensionElementsList(loopCharacteristics, 'zeebe:LoopCharacteristics');

  return extensionElements && extensionElements[0];
}

function supportsMultiInstances(element) {
  return !!getLoopCharacteristics(element);
}

function getCompletionCondition(element) {
  return getLoopCharacteristics(element).get('completionCondition');
}

function getProperty(element, propertyName) {
  const loopCharacteristics = getLoopCharacteristics(element),
        zeebeLoopCharacteristics = getZeebeLoopCharacteristics(loopCharacteristics);

  return zeebeLoopCharacteristics && zeebeLoopCharacteristics.get(propertyName);
}

function setProperty(element, propertyName, value, commandStack, bpmnFactory) {
  const loopCharacteristics = getLoopCharacteristics(element);

  const commands = [];

  // (1) ensure extension elements
  let extensionElements = loopCharacteristics.get('extensionElements');
  if (!extensionElements) {
    extensionElements = createElement(
      'bpmn:ExtensionElements',
      { values: [] },
      loopCharacteristics,
      bpmnFactory
    );

    commands.push({
      cmd: 'element.updateModdleProperties',
      context: {
        element,
        moddleElement: loopCharacteristics,
        properties: { extensionElements }
      }
    });
  }

  // (2) ensure zeebe loop characteristics
  let zeebeLoopCharacteristics = getZeebeLoopCharacteristics(loopCharacteristics);
  if (!zeebeLoopCharacteristics) {
    zeebeLoopCharacteristics = createElement(
      'zeebe:LoopCharacteristics',
      { },
      extensionElements,
      bpmnFactory
    );

    commands.push({
      cmd: 'element.updateModdleProperties',
      context: {
        element,
        moddleElement: extensionElements,
        properties: {
          values: [ ...extensionElements.get('values'), zeebeLoopCharacteristics ]
        }
      }
    });
  }

  // (3) update defined property
  commands.push({
    cmd: 'element.updateModdleProperties',
    context: {
      element,
      moddleElement: zeebeLoopCharacteristics,
      properties: { [ propertyName ]: value }
    }
  });

  // (4) commit all updates
  commandStack.execute('properties-panel.multi-command-executor', commands);
}
